import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

import { getLoginUrl } from '@/config/auth'
import { LOGO_PUBLIC_SRC } from '@/config/branding'
import { env } from '@/config/env'
import { useAuth } from '@/hooks/useAuth'

export function LoginPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isAuthenticated, signInLocal } = useAuth()
  const oauthReady = Boolean(env.oauthPortalUrl && env.oauthAppId)

  useEffect(() => {
    if (isAuthenticated) navigate('/assets', { replace: true })
  }, [isAuthenticated, navigate])

  const handleGoogleLogin = () => {
    window.location.href = getLoginUrl()
  }

  const handleDemo = () => {
    signInLocal({ name: 'Demo User' })
    navigate('/assets', { replace: true })
  }

  return (
    <div className="flex min-h-screen flex-col" style={{ background: '#F0F4FF' }}>
      <main className="flex flex-1 items-center justify-center px-4" style={{ paddingTop: '80px', paddingBottom: '60px' }}>
        <div className="w-full max-w-md">
          <div
            className="rounded-3xl p-8 sm:p-10"
            style={{
              background: 'white',
              boxShadow: '0 4px 40px rgba(26, 86, 255, 0.08)',
              border: '1px solid rgba(26, 86, 255, 0.08)',
            }}
          >
            <div className="mb-8 flex justify-center">
              <div className="flex items-center gap-3">
                <img src={LOGO_PUBLIC_SRC} alt={t('common.brandAlt')} className="h-12 w-12 rounded-2xl object-cover" width={48} height={48} />
                <span
                  style={{
                    fontFamily: 'Outfit, sans-serif',
                    fontWeight: 900,
                    fontSize: '24px',
                    color: '#0D1B3E',
                    letterSpacing: '-0.02em',
                  }}
                >
                  {t('common.appName')}
                </span>
              </div>
            </div>

            <div className="mb-8 text-center">
              <h1
                style={{
                  fontFamily: 'Outfit, sans-serif',
                  fontWeight: 800,
                  fontSize: '26px',
                  color: '#0D1B3E',
                  letterSpacing: '-0.03em',
                  marginBottom: '8px',
                }}
              >
                {t('login.welcome')}
              </h1>
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '15px', color: '#6B7A99', lineHeight: 1.6 }}>
                {t('login.subtitle')}
              </p>
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '12px', color: '#9BA8BE', lineHeight: 1.6, marginTop: '10px' }}>
                {t('login.googleOnly')}
              </p>
            </div>

            {!oauthReady ? (
              <p className="mb-4 rounded-2xl border border-dashed border-[#1A56FF]/30 bg-[#1A56FF]/5 p-4 text-sm text-[#6B7A99]">
                {t('login.missingOAuth')}
              </p>
            ) : null}

            <div className="mb-6 flex items-center gap-3">
              <div className="h-px flex-1" style={{ background: 'rgba(26, 86, 255, 0.08)' }} />
              <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '12px', color: '#9BA8BE', fontWeight: 500 }}>
                {t('login.continueWith')}
              </span>
              <div className="h-px flex-1" style={{ background: 'rgba(26, 86, 255, 0.08)' }} />
            </div>

            <button
              type="button"
              onClick={handleGoogleLogin}
              disabled={!oauthReady}
              className="flex w-full items-center justify-center gap-3 rounded-2xl py-4 transition-all duration-200"
              style={{
                background: 'white',
                border: '1.5px solid rgba(26, 86, 255, 0.15)',
                cursor: oauthReady ? 'pointer' : 'not-allowed',
                opacity: oauthReady ? 1 : 0.55,
                boxShadow: '0 2px 12px rgba(26, 86, 255, 0.06)',
              }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              <span style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: '16px', color: '#0D1B3E' }}>
                {t('login.google')}
              </span>
            </button>

            {env.isDev ? (
              <button
                type="button"
                className="mt-3 w-full rounded-2xl border border-dashed border-[#1A56FF]/40 py-3 text-sm font-semibold text-[#1A56FF]"
                onClick={handleDemo}
              >
                {t('login.demo')}
              </button>
            ) : null}

            <p
              className="mt-6 text-center"
              style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '12px', color: '#9BA8BE', lineHeight: 1.6 }}
            >
              {t('login.agreePrefix')}{' '}
              <Link to="/terms-of-service" style={{ color: '#1A56FF', textDecoration: 'none' }}>
                {t('nav.terms')}
              </Link>{' '}
              {t('login.agreeAnd')}{' '}
              <Link to="/privacy-policy" style={{ color: '#1A56FF', textDecoration: 'none' }}>
                {t('nav.privacy')}
              </Link>
            </p>
          </div>

          <div className="mt-6 text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5"
              style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14px', color: '#6B7A99', textDecoration: 'none' }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="M19 12H5M12 5l-7 7 7 7" stroke="#6B7A99" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {t('login.backHome')}
            </Link>
          </div>
        </div>
      </main>
    </div>
  )
}
